import React from 'react';
import { Box, Container, Typography } from '@mui/material';

const formatInr = (amount) =>
    new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
    }).format(amount);

const PLANS = [
    {
        name: 'Standard',
        price: 7500,
        therapistLine: 'Up to 5 therapists',
        description:
            'Every session verified, every payout calculated from a confirmed record. No more disputed cycles.',
        recommended: false,
    },
    {
        name: 'Plus',
        price: 12000,
        therapistLine: 'Up to 15 therapists',
        description:
            'Payroll calculated automatically from verified sessions. Payouts processed through the platform. The dispute ends before it starts.',
        recommended: true,
    },
    {
        name: 'Premium',
        price: 18000,
        therapistLine: 'Up to 50 therapists',
        description:
            'Full visibility across sessions, records, and payouts for up to 50 therapists. Nothing requires chasing.',
        recommended: false,
    },
    {
        name: 'Enterprise',
        price: 45000,
        therapistLine: 'Unlimited staff',
        description:
            "Custom reporting, multi-language consent, and a 2-hour support SLA. For large operations that can't afford downtime.",
        recommended: false,
    },
];

const cardTransition =
    'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.28s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.28s ease';

const PlansSection = () => {
    return (
        <Box
            component="section"
            aria-labelledby="pricing-plans-heading"
            sx={{
                pt: { xs: 5, md: 6.5, lg: 7 },
                pb: { xs: 4, md: 4.5, lg: 4.5 },
                background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFF 40%, #F0F4FF 100%)',
                borderTop: '1px solid rgba(0, 102, 255, 0.08)',
            }}
        >
            <Container maxWidth="xl">
                <Box sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto', mb: { xs: 5, md: 6 } }}>
                    <Typography
                        component="span"
                        sx={{
                            display: 'inline-block',
                            fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                            fontWeight: 700,
                            letterSpacing: '0.18em',
                            textTransform: 'uppercase',
                            color: '#0066FF',
                            bgcolor: 'rgba(0, 102, 255, 0.1)',
                            px: { xs: 1.75, sm: 2.25 },
                            py: 0.75,
                            borderRadius: 999,
                            mb: 2,
                        }}
                    >
                        Plans
                    </Typography>
                    <Typography
                        id="pricing-plans-heading"
                        component="h2"
                        sx={{
                            fontSize: { xs: '1.75rem', sm: '2.125rem', md: '2.5rem' },
                            fontWeight: 700,
                            letterSpacing: '-0.03em',
                            lineHeight: 1.15,
                            color: '#111827',
                            m: 0,
                            mb: 1.5,
                        }}
                    >
                        Monthly pricing. No setup fees.
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(0, 1fr))',
                            lg: 'repeat(4, minmax(0, 1fr))',
                        },
                        gap: { xs: 2.5, md: 3 },
                        alignItems: 'stretch',
                        pt: { xs: 2, md: 2.5 },
                    }}
                >
                    {PLANS.map((plan) => (
                        <Box
                            key={plan.name}
                            sx={{
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                minWidth: 0,
                            }}
                        >
                            <Box
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    width: '100%',
                                    minHeight: '100%',
                                    borderRadius: '20px',
                                    p: { xs: 2.5, md: 3 },
                                    pt: plan.recommended ? { xs: 3.5, md: 3.75 } : { xs: 2.5, md: 3 },
                                    background: plan.recommended
                                        ? 'linear-gradient(165deg, #FFFFFF 0%, #F5F8FF 55%, #EEF2FF 100%)'
                                        : '#FFFFFF',
                                    border: plan.recommended
                                        ? '1.5px solid rgba(0, 102, 255, 0.28)'
                                        : '1px solid rgba(15, 23, 42, 0.08)',
                                    boxShadow: plan.recommended
                                        ? '0 16px 48px rgba(0, 102, 255, 0.12), 0 4px 16px rgba(15, 23, 42, 0.06)'
                                        : '0 4px 24px rgba(15, 23, 42, 0.06)',
                                    transition: cardTransition,
                                    overflow: plan.recommended ? 'visible' : 'hidden',
                                    ...(plan.recommended && {
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            inset: 0,
                                            borderRadius: 'inherit',
                                            padding: '1px',
                                            background:
                                                'linear-gradient(145deg, rgba(0, 102, 255, 0.35), rgba(99, 102, 241, 0.12))',
                                            WebkitMask:
                                                'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                            WebkitMaskComposite: 'xor',
                                            maskComposite: 'exclude',
                                            pointerEvents: 'none',
                                            opacity: 0.5,
                                        },
                                    }),
                                    '&:hover': {
                                        transform: 'translateY(-6px)',
                                        boxShadow: plan.recommended
                                            ? '0 24px 56px rgba(0, 102, 255, 0.16), 0 8px 24px rgba(15, 23, 42, 0.08)'
                                            : '0 20px 48px rgba(15, 23, 42, 0.1)',
                                        borderColor: plan.recommended
                                            ? 'rgba(0, 102, 255, 0.45)'
                                            : 'rgba(0, 102, 255, 0.2)',
                                    },
                                }}
                            >
                                {plan.recommended && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            zIndex: 2,
                                            px: 2,
                                            py: 0.5,
                                            borderRadius: 999,
                                            fontSize: '0.6875rem',
                                            fontWeight: 700,
                                            letterSpacing: '0.14em',
                                            textTransform: 'uppercase',
                                            color: '#FFFFFF',
                                            background: 'linear-gradient(135deg, #0066FF 0%, #0052CC 100%)',
                                            boxShadow: '0 6px 20px rgba(0, 102, 255, 0.35)',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Recommended
                                    </Box>
                                )}
                                <Typography
                                    component="h3"
                                    sx={{
                                        fontSize: '1.25rem',
                                        fontWeight: 700,
                                        letterSpacing: '-0.02em',
                                        color: '#111827',
                                        m: 0,
                                        mb: 1.5,
                                    }}
                                >
                                    {plan.name}
                                </Typography>

                                <Box sx={{ mb: 2 }}>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: { xs: '2rem', md: '2.25rem' },
                                            fontWeight: 700,
                                            letterSpacing: '-0.03em',
                                            color: '#0F172A',
                                            lineHeight: 1.1,
                                        }}
                                    >
                                        {formatInr(plan.price)}
                                    </Typography>
                                    <Typography
                                        component="span"
                                        sx={{
                                            display: 'block',
                                            mt: 0.5,
                                            fontSize: '0.9375rem',
                                            fontWeight: 500,
                                            color: '#6B7280',
                                        }}
                                    >
                                        per month
                                    </Typography>
                                </Box>

                                <Typography
                                    sx={{
                                        fontSize: '0.8125rem',
                                        fontWeight: 700,
                                        letterSpacing: '0.06em',
                                        textTransform: 'uppercase',
                                        color: '#0066FF',
                                        m: 0,
                                        mb: 2,
                                    }}
                                >
                                    {plan.therapistLine}
                                </Typography>

                                <Typography
                                    component="p"
                                    sx={{
                                        m: 0,
                                        flex: 1,
                                        fontSize: '0.9375rem',
                                        lineHeight: 1.68,
                                        color: 'rgba(15, 23, 42, 0.76)',
                                    }}
                                >
                                    {plan.description}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Container>
        </Box>
    );
};

export default PlansSection;
